import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, Select, message } from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getPlanManagementTasks } from 'enrollment-platform/pages/PlanManagementV2/api/plan-management-tasks-api'
import { PlanManagementTaskStatusEnum } from 'enrollment-platform/pages/PlanManagementV2/api/enums/plan-management-task-status.enum'
import { US_STATE_OPTIONS } from 'enrollment-platform/pages/PlanManagementV2/pages/PlanOverrides/constants'
import { downloadSurecoCsv } from '../api/plan-management-v3-api'
import {
  DOWNLOADABLE_SURECO_CSV_DATA_TYPE_OPTIONS,
  type DownloadableSurecoCsvDataType,
} from '../constants'
import styles from '../PlanManagementV3.module.scss'

const VERSIONS_PAGE_SIZE = 100

interface Props {
  open: boolean
  onClose: () => void
}

export default function DownloadSurecoCsvModal({ open, onClose }: Props) {
  const [taskId, setTaskId] = useState<string | undefined>(undefined)
  const [dataType, setDataType] = useState<
    DownloadableSurecoCsvDataType | undefined
  >(undefined)
  const [states, setStates] = useState<string[]>([])
  const [isDownloading, setIsDownloading] = useState(false)

  const {
    data: versions,
    isLoading: isLoadingVersions,
    error: versionsError,
  } = useQuery({
    queryKey: ['surecoCsvFetchVersions'],
    queryFn: () =>
      getPlanManagementTasks({
        page: 1,
        limit: VERSIONS_PAGE_SIZE,
        sort: 'createdAt',
        order: 'DESC',
        status: PlanManagementTaskStatusEnum.SUCCESS,
      }),
    enabled: open,
  })

  const versionRows = versions?.rows ?? []

  // Only auto-select once per open session, in case versionRows refetches
  // after the user has already chosen a different version.
  const hasAutoSelectedRef = useRef(false)
  useEffect(() => {
    if (open && versionRows.length > 0 && !hasAutoSelectedRef.current) {
      setTaskId(versionRows[0].id)
      hasAutoSelectedRef.current = true
    }
  }, [open, versionRows])

  useEffect(() => {
    if (versionsError) {
      console.error('Could not load fetch versions', versionsError)
      message.error('An error occurred. Please try again.')
    }
  }, [versionsError])

  const handleClose = () => {
    if (isDownloading) return
    setTaskId(undefined)
    setDataType(undefined)
    setStates([])
    hasAutoSelectedRef.current = false
    onClose()
  }

  const handleDownload = async () => {
    if (taskId === undefined || dataType === undefined) return
    const year = versionRows.find((row) => row.id === taskId)?.planYear
    if (year === undefined) {
      message.error('Could not determine the plan year for this version.')
      return
    }
    setIsDownloading(true)
    try {
      await downloadSurecoCsv({ taskId, dataType, year, states })
      handleClose()
    } catch (err) {
      console.error('Failed to download sureco csv', err)
      message.error('An error occurred. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const isDownloadDisabled =
    isLoadingVersions ||
    !!versionsError ||
    isDownloading ||
    taskId === undefined ||
    dataType === undefined

  return (
    <Modal
      open={open}
      title="Download CSV"
      maskClosable={false}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" htmlType="button" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          htmlType="button"
          type="primary"
          disabled={isDownloadDisabled}
          loading={isDownloading}
          onClick={() => void handleDownload()}
        >
          Download
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Fetch version</span>
          <Select
            placeholder="Select a fetch version"
            loading={isLoadingVersions}
            value={taskId}
            onChange={(v) => setTaskId(v)}
            options={versionRows.map((row) => ({
              label: `${dayjs(row.createdAt).format('MMM D, YYYY h:mm A')} · PY${row.planYear}`,
              value: row.id,
            }))}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Data type</span>
          <Select
            placeholder="Select a data type"
            value={dataType}
            onChange={(v) => setDataType(v)}
            options={DOWNLOADABLE_SURECO_CSV_DATA_TYPE_OPTIONS}
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>State</span>
          <Select
            mode="multiple"
            placeholder="Nationwide"
            showSearch
            optionFilterProp="label"
            value={states}
            onChange={(v) => setStates(v)}
            options={US_STATE_OPTIONS}
            maxTagCount="responsive"
            dropdownRender={(menu) => (
              <>
                <div className={styles.selectAllRow}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setStates([])}
                  >
                    Clear all
                  </Button>
                </div>
                {menu}
              </>
            )}
          />
        </div>
      </div>
    </Modal>
  )
}
